<script setup>
/**
 * Pipeline Reports - Relatórios de Pipeline/Kanban
 * Usando novo design visual moderno com gráficos
 */
import { ref, onMounted, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { createLatestRequest } from 'dashboard/helper/latestRequest';
import kanbanReportsAPI from 'dashboard/api/kanbanReports';

import V4Button from 'dashboard/components-next/button/Button.vue';
import KanbanReportFilters from './components/kanban/KanbanReportFilters.vue';
import KanbanSummaryMetrics from './components/kanban/KanbanSummaryMetrics.vue';
import KanbanCommercialOverview from './components/kanban/KanbanCommercialOverview.vue';
import KanbanCharts from './components/kanban/KanbanCharts.vue';
import KanbanAgentTable from './components/kanban/KanbanAgentTable.vue';
import KanbanFunnelTable from './components/kanban/KanbanFunnelTable.vue';
import KanbanStageTable from './components/kanban/KanbanStageTable.vue';
import SalesAttributionReport from './components/kanban/SalesAttributionReport.vue';
import ProposalConversionReport from './components/kanban/ProposalConversionReport.vue';
import ProposalReceiptsReport from './components/kanban/ProposalReceiptsReport.vue';

const { t } = useI18n();

// Estado
const isLoading = ref(false);
const isDownloadingPdf = ref(false);
const summaryData = ref(null);
const byFunnelData = ref(null);
const byStageData = ref(null);
const byAgentData = ref(null);
const conversionFunnelData = ref(null);
const stageDistributionData = ref(null);
const timeInStagesData = ref(null);
const salesAttributionData = ref(null);
const proposalConversionData = ref(null);
const proposalReceiptsData = ref(null);
const commercialOverviewData = ref(null);
const paymentEnvironment = ref('production');
const tracker = createLatestRequest();

// Inicializar datas padrão (últimos 30 dias)
const getDefaultDates = () => {
  const now = new Date();
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(now.getDate() - 30);

  return {
    since: thirtyDaysAgo.getTime(),
    until: now.getTime(),
  };
};

const defaultDates = getDefaultDates();

// Filtros
const filters = ref({
  since: defaultDates.since,
  until: defaultDates.until,
  funnel_ids: [],
  agent_ids: [],
  product_ids: [],
  min_value: null,
  max_value: null,
});

// Computed
// Há conteúdo quando QUALQUER bloco carregou; um bloco que falhou não esconde os demais.
const hasData = computed(() =>
  [
    summaryData,
    byFunnelData,
    byStageData,
    byAgentData,
    conversionFunnelData,
    stageDistributionData,
    timeInStagesData,
    salesAttributionData,
    proposalConversionData,
    proposalReceiptsData,
    commercialOverviewData,
  ].some(block => block.value !== null)
);

// Helper functions
const buildParams = () => {
  const params = { payment_environment: paymentEnvironment.value };

  if (filters.value.since) {
    params.since = Math.floor(filters.value.since / 1000);
  }

  if (filters.value.until) {
    // floor: 23:59:59.999 com ceil virava 00:00 do dia seguinte e o
    // relatório somava um dia a mais.
    params.until = Math.floor(filters.value.until / 1000);
  }

  if (filters.value.funnel_ids && filters.value.funnel_ids.length > 0) {
    params.funnel_ids = filters.value.funnel_ids;
  }

  if (filters.value.agent_ids && filters.value.agent_ids.length > 0) {
    params.agent_ids = filters.value.agent_ids;
  }

  if (filters.value.product_ids && filters.value.product_ids.length > 0) {
    params.product_ids = filters.value.product_ids;
  }

  if (
    filters.value.min_value !== null &&
    filters.value.min_value !== undefined &&
    filters.value.min_value !== ''
  ) {
    params.min_value = filters.value.min_value;
  }

  if (
    filters.value.max_value !== null &&
    filters.value.max_value !== undefined &&
    filters.value.max_value !== ''
  ) {
    params.max_value = filters.value.max_value;
  }

  return params;
};

// Métodos
const fetchAllReports = async () => {
  const id = tracker.next();
  isLoading.value = true;
  const params = buildParams();

  // Um par [bloco, chamada] por bloco da tela: o que falhar vira null e os
  // demais continuam atualizados.
  const blocks = [
    [summaryData, () => kanbanReportsAPI.getSummary(params)],
    [byFunnelData, () => kanbanReportsAPI.getByFunnel(params)],
    [byStageData, () => kanbanReportsAPI.getByStage(params)],
    [byAgentData, () => kanbanReportsAPI.getByAgent(params)],
    [conversionFunnelData, () => kanbanReportsAPI.getConversionFunnel(params)],
    [
      stageDistributionData,
      () => kanbanReportsAPI.getStageDistribution(params),
    ],
    [timeInStagesData, () => kanbanReportsAPI.getTimeInStages(params)],
    [salesAttributionData, () => kanbanReportsAPI.getSalesAttribution(params)],
    [
      proposalConversionData,
      () => kanbanReportsAPI.getProposalConversion(params),
    ],
    [proposalReceiptsData, () => kanbanReportsAPI.getProposalReceipts(params)],
    [
      commercialOverviewData,
      () => kanbanReportsAPI.getCommercialOverview(params),
    ],
  ];

  const results = await Promise.allSettled(blocks.map(([, call]) => call()));
  if (!tracker.isLatest(id)) return;

  let failed = 0;
  results.forEach((result, index) => {
    blocks[index][0].value =
      result.status === 'fulfilled' ? result.value.data : null;
    if (result.status === 'rejected') failed += 1;
  });
  if (failed) useAlert(t('KANBAN_REPORTS.PARTIAL_ERROR', { count: failed }));
  isLoading.value = false;
};

const onEnvironmentChange = environment => {
  paymentEnvironment.value = environment;
  fetchAllReports();
};

const onFilterChange = newFilters => {
  filters.value = newFilters;
  fetchAllReports();
};

const downloadPdf = async () => {
  if (!hasData.value) {
    useAlert(t('KANBAN_REPORTS.NO_DATA_TO_EXPORT'));
    return;
  }

  isDownloadingPdf.value = true;

  try {
    const params = buildParams();
    const response = await kanbanReportsAPI.downloadPdf(params);

    // Create blob and download
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `pipeline_report_${Date.now()}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    useAlert(t('KANBAN_REPORTS.PDF_DOWNLOADED_SUCCESSFULLY'));
  } catch (error) {
    useAlert(t('KANBAN_REPORTS.ERROR_DOWNLOADING_PDF'));
  } finally {
    isDownloadingPdf.value = false;
  }
};

// Lifecycle
onMounted(() => {
  fetchAllReports();
});
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-auto bg-n-background">
    <!-- Header -->
    <header
      class="sticky top-0 z-10 px-6 py-4 border-b border-n-weak bg-n-solid-1"
    >
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-lg font-semibold text-n-slate-12">
            {{ $t('KANBAN_REPORTS.HEADER') }}
          </h1>
          <p class="text-sm text-n-slate-11">
            {{ $t('KANBAN_REPORTS.DESCRIPTION') }}
          </p>
        </div>
        <V4Button
          :label="$t('KANBAN_REPORTS.DOWNLOAD_PDF')"
          icon="i-ph-download-simple"
          size="sm"
          :loading="isDownloadingPdf"
          :disabled="!hasData || isDownloadingPdf"
          @click="downloadPdf"
        />
      </div>
    </header>

    <!-- Content -->
    <div class="flex-1 p-6 space-y-6">
      <!-- Filtros -->
      <KanbanReportFilters
        :filters="filters"
        :is-loading="isLoading"
        @filter-change="onFilterChange"
      />

      <div v-if="isLoading" class="h-0.5 w-full animate-pulse bg-n-brand" />

      <!-- Loading State -->
      <div
        v-if="isLoading && !hasData"
        class="flex items-center justify-center py-20"
      >
        <div class="flex flex-col items-center gap-3">
          <div
            class="w-8 h-8 border-2 border-n-brand border-t-transparent rounded-full animate-spin"
          />
          <span class="text-sm text-n-slate-11">
            {{ $t('KANBAN_REPORTS.LOADING') }}
          </span>
        </div>
      </div>

      <!-- Conteúdo -->
      <template v-else-if="hasData">
        <!-- Visão comercial: o resumo que a diretoria olha primeiro -->
        <KanbanCommercialOverview
          :report="commercialOverviewData"
          :is-loading="isLoading"
        />

        <!-- Métricas de Resumo -->
        <KanbanSummaryMetrics :summary="summaryData" :is-loading="isLoading" />

        <SalesAttributionReport
          v-if="salesAttributionData"
          :report="salesAttributionData"
        />
        <ProposalConversionReport
          :report="proposalConversionData"
          :is-loading="isLoading"
        />

        <ProposalReceiptsReport
          :report="proposalReceiptsData"
          :environment="paymentEnvironment"
          :is-loading="isLoading"
          @update:environment="onEnvironmentChange"
        />

        <!-- Gráficos -->
        <KanbanCharts
          :conversion-funnel="conversionFunnelData"
          :stage-distribution="stageDistributionData"
          :time-in-stages="timeInStagesData"
          :is-loading="isLoading"
        />

        <!-- Tabelas -->
        <div class="grid grid-cols-1 gap-4">
          <!-- Tabela de Agentes -->
          <KanbanAgentTable :data="byAgentData" :is-loading="isLoading" />

          <!-- Tabela de Funis -->
          <KanbanFunnelTable :data="byFunnelData" :is-loading="isLoading" />

          <!-- Tabela de Etapas -->
          <KanbanStageTable :data="byStageData" :is-loading="isLoading" />
        </div>
      </template>

      <!-- Empty State -->
      <div
        v-else
        class="flex flex-col items-center justify-center py-20 rounded-xl border border-n-weak bg-n-solid-2"
      >
        <div
          class="w-16 h-16 rounded-full bg-n-slate-3 flex items-center justify-center mb-4"
        >
          <span class="i-ph-funnel-duotone text-3xl text-n-slate-9" />
        </div>
        <h3 class="text-xl font-semibold text-n-slate-12 mb-2">
          {{ $t('KANBAN_REPORTS.EMPTY_STATE.TITLE') }}
        </h3>
        <p class="text-sm text-n-slate-11 text-center max-w-md">
          {{ $t('KANBAN_REPORTS.EMPTY_STATE.MESSAGE') }}
        </p>
      </div>
    </div>
  </div>
</template>
